import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { usePermission } from '@/components/guards/permission-guard';
import { showErrorToast, showSuccessToast } from '@/hooks/use-mutation-with-toast';

export type Direction = 'INCOMING' | 'OUTGOING';
export type DirectionFilter = Direction | 'UNASSIGNED';
export const parseDirection = (value: string | null): Direction | undefined => value === 'INCOMING' || value === 'OUTGOING' ? value : undefined;
export const parseDirectionFilter = (value: string | null): DirectionFilter | undefined => value === 'UNASSIGNED' ? value : parseDirection(value);

export function DirectionSelect({ value, onChange, filter = false, disabled = false }: {
  value?: string | null; onChange: (value: string) => void; filter?: boolean; disabled?: boolean;
}) {
  const { i18n } = useTranslation();
  const tr = i18n.language.startsWith('tr');
  return <label className="flex items-center gap-3 text-sm">
    <span>{tr ? 'Seyahat yönü' : 'Travel direction'}</span>
    <select aria-label={tr ? 'Seyahat yönü' : 'Travel direction'} value={value ?? ''} disabled={disabled} onChange={e => onChange(e.target.value)} className="h-9 border bg-background px-3">
      {filter && <option value="">{tr ? 'Tümü' : 'All'}</option>}
      <option value={filter ? 'UNASSIGNED' : ''}>{tr ? 'Belirtilmemiş' : 'Unassigned'}</option>
      <option value="OUTGOING">Outgoing</option><option value="INCOMING">Incoming</option>
    </select>
  </label>;
}

export function DirectionEditor({ kind, id, value, booking }: {
  kind: 'booking' | 'invoice'; id: string; value?: string | null;
  booking?: { id: string; direction?: string | null } | null;
}) {
  const { i18n } = useTranslation(); const tr = i18n.language.startsWith('tr');
  const canEdit = usePermission(kind === 'booking' ? 'BOOKINGS' : 'INVOICES', 'EDIT');
  const utils = trpc.useUtils();
  const options = {
    onSuccess: () => {
      utils.booking.invalidate(); utils.invoice.invalidate(); utils.hotel.invalidate(); utils.flight.invalidate();
      showSuccessToast(tr ? 'Seyahat yönü güncellendi.' : 'Travel direction updated.');
    },
    onError: (error: { message: string }) => showErrorToast(error.message),
  };
  const bookingMutation = trpc.booking.setDirection.useMutation(options);
  const invoiceMutation = trpc.invoice.setDirection.useMutation(options);
  return <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-3">
    <DirectionSelect value={booking ? booking.direction : value} disabled={!canEdit || !!booking || bookingMutation.isPending || invoiceMutation.isPending}
      onChange={raw => { const direction = parseDirection(raw) ?? null; (kind === 'booking' ? bookingMutation : invoiceMutation).mutate({ id, direction }); }} />
    {booking && <Link className="text-sm underline" to={`/travel/bookings/${booking.id}`}>{tr ? 'Yönü bağlı rezervasyondan değiştir' : 'Change direction on the linked booking'}</Link>}
  </div>;
}

export type TourType = 'LAND' | 'CRUISE';
export const parseTourType = (value: string | null): TourType | undefined => value === 'LAND' || value === 'CRUISE' ? value : undefined;
export function TourTypeSelect({ value, onChange, filter = false }: { value?: string | null; onChange: (value: string) => void; filter?: boolean }) {
  const { i18n } = useTranslation(); const tr = i18n.language.startsWith('tr');
  return <label className="flex items-center gap-3 text-sm"><span>{tr ? 'Tur türü' : 'Tour type'}</span>
    <select aria-label={tr ? 'Tur türü' : 'Tour type'} value={value ?? ''} onChange={e => onChange(e.target.value)} className="h-9 border bg-background px-3">
      {filter && <option value="">{tr ? 'Tümü' : 'All'}</option>}
      <option value={filter ? 'UNASSIGNED' : ''}>{tr ? 'Belirtilmemiş' : 'Unassigned'}</option>
      <option value="LAND">{tr ? 'Kara Turları' : 'Land Tours'}</option><option value="CRUISE">Cruise</option>
    </select></label>;
}
